import { supabase } from '../../lib/supabase'
import { expandDateRange, getEmployeeTeamMap } from '../supervisor/leaveRequestQueue'
import { groupScheduledDays, type LeaveDecision, type LeaveRecord, type LeaveRequestState } from './leaveFigures'

type DecisionRow = { decision: LeaveDecision | 'reverse'; decided_at: string; decided_by_name: string; modified_dates: string[] | null }

type RequestRow = {
  id: string
  employee_id: string
  start_date: string
  end_date: string
  state: LeaveRequestState
  full_day: boolean
  leave_types: { name: string } | null
  employees: { display_name: string; employee_identifier: string } | null
  decisions: DecisionRow[] | null
}

/**
 * Leave as requested: every request the signed-in account can read, with its
 * latest decision. The team is the employee's placement today. An approval
 * with modification covers only the dates the supervisor approved.
 */
export async function getRequestedLeave(from: string, to: string): Promise<LeaveRecord[]> {
  const [teamMap, result] = await Promise.all([
    getEmployeeTeamMap(),
    supabase
      .from('leave_requests')
      .select(
        'id, employee_id, start_date, end_date, state, full_day, leave_types(name), employees(display_name, employee_identifier), decisions(decision, decided_at, decided_by_name, modified_dates)',
      )
      .lte('start_date', to)
      .gte('end_date', from)
      .order('start_date', { ascending: true }),
  ])
  if (result.error) throw new Error('The leave report could not be loaded.')

  return ((result.data ?? []) as unknown as RequestRow[]).flatMap((row) => {
    const team = teamMap.get(row.employee_id)
    if (!team || !row.employees) return []
    // A reopened, resubmitted request can gather more than one decision. A
    // latest one that reverses an earlier decision means none stands, and the
    // request is pending again.
    const latest = (row.decisions ?? []).reduce<DecisionRow | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    const decision = latest && latest.decision !== 'reverse' ? (latest as DecisionRow & { decision: LeaveDecision }) : undefined
    return [
      {
        id: row.id,
        employeeId: row.employee_id,
        identifier: row.employees.employee_identifier,
        employee: row.employees.display_name,
        teamId: team.teamId,
        team: team.teamName,
        dates: decision?.modified_dates ?? expandDateRange(row.start_date, row.end_date),
        leaveType: row.leave_types?.name ?? 'Other approved absence',
        fullDay: row.full_day,
        state: row.state,
        decision: decision?.decision ?? null,
        decidedBy: decision?.decided_by_name ?? null,
        decidedAt: decision?.decided_at ?? null,
      },
    ]
  })
}

type VersionRow = {
  team_id: string
  period_start: string
  version: number
  teams: { name: string } | null
  entries: { employeeId: string; employeeName: string; date: string; category: string; label: string }[]
}

/**
 * Leave as scheduled: the leave entries in each team's latest published
 * version of every pay period the range touches.
 */
export async function getScheduledLeave(from: string, to: string): Promise<LeaveRecord[]> {
  const { data, error } = await supabase
    .from('schedule_versions')
    .select('team_id, period_start, version, teams(name), entries')
    .lte('period_start', to)
    .gte('period_end', from)
  if (error) throw new Error('The leave report could not be loaded.')

  const latest = new Map<string, VersionRow>()
  for (const row of (data ?? []) as unknown as VersionRow[]) {
    const key = `${row.team_id}|${row.period_start}`
    const current = latest.get(key)
    if (!current || row.version > current.version) latest.set(key, row)
  }

  const days = Array.from(latest.values()).flatMap((version) =>
    (version.entries ?? [])
      .filter((entry) => entry.category === 'leave' && entry.employeeId && entry.date >= from && entry.date <= to)
      .map((entry) => ({
        employeeId: entry.employeeId,
        employee: entry.employeeName,
        teamId: version.team_id,
        team: version.teams?.name ?? '',
        date: entry.date,
        leaveType: entry.label,
      })),
  )
  if (days.length === 0) return []

  const { data: employees, error: employeeError } = await supabase
    .from('employees')
    .select('id, employee_identifier')
    .in('id', Array.from(new Set(days.map((day) => day.employeeId))))
  if (employeeError) throw new Error('The leave report could not be loaded.')
  const identifiers = new Map(((employees ?? []) as { id: string; employee_identifier: string }[]).map((row) => [row.id, row.employee_identifier]))

  return groupScheduledDays(days.map((day) => ({ ...day, identifier: identifiers.get(day.employeeId) ?? '' })))
}
